import React, { useEffect, useRef } from 'react';

import type { VariantType } from 'notistack';
import { SnackbarProvider, useSnackbar } from 'notistack';

interface Message {
  id: string;
  message: string;
  type: VariantType;
  autoClose: number;
}

interface Messages {
  [key: string]: Message;
}

interface SnackbarWrapperProps {
  messages?: Messages;
  removeMessage?: (key: string) => void;
}

interface SnackbarProps {
  messages?: Messages;
  removeMessage?: (key: string) => void;
  vertical?: 'top' | 'bottom';
  horizontal?: 'left' | 'center' | 'right';
}

const SnackbarWrapper = ({
  messages = {},
  removeMessage = () => {},
}: SnackbarWrapperProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const prevMessages = usePrevious(messages);

  useEffect(() => {
    getMessagesAsArray(messages, prevMessages).map(
      ({ id, message, type, autoClose }) => {
        enqueueSnackbar(message, {
          key: id,
          variant: type,
          autoHideDuration: autoClose,
          onClose: (event, reason, key) => removeMessage(key as string),
        });
      },
    );
  }, [messages, enqueueSnackbar, prevMessages, removeMessage]);

  return null;
};

const Snackbar = ({
  messages = {},
  vertical = 'bottom',
  horizontal = 'right',
  removeMessage = () => {},
}: SnackbarProps) => {
  return (
    <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical, horizontal }}>
      <SnackbarWrapper messages={messages} removeMessage={removeMessage} />
    </SnackbarProvider>
  );
};

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function getMessagesAsArray(
  newMessages: Messages,
  prevMessages?: Messages,
): Message[] {
  const messages = Object.values(newMessages);
  return messages.filter((message) => !prevMessages?.[message.id]);
}

export default Snackbar;
